/**
 * Global Error Handler Utility for Google Advanced Reporting System (GARS)
 *
 * Extracts error messages with strict first-priority given to backend-returned
 * error responses, followed by HTTP status code-based explanations, and finally
 * contextual frontend fallback messages.
 */

export function extractApiErrorMessage(
  err: unknown,
  fallbackMessage: string = 'An unexpected error occurred. Please try again.'
): string {
  if (!err) {
    return fallbackMessage;
  }

  // If already a plain non-empty string
  if (typeof err === 'string') {
    const trimmed = err.trim();
    if (trimmed) return trimmed;
  }

  const anyErr: any = err;

  // ── PRIORITY 1: Backend error response body (Angular HttpErrorResponse.error) ──
  if (anyErr.error !== undefined && anyErr.error !== null) {
    // Plain text backend response
    if (typeof anyErr.error === 'string') {
      const trimmed = anyErr.error.trim();
      // Ignore raw HTML pages (e.g. Nginx/Gateway 502/504 Bad Gateway default HTML)
      if (trimmed && !trimmed.startsWith('<!DOCTYPE') && !trimmed.startsWith('<html')) {
        return trimmed;
      }
    } else if (typeof anyErr.error === 'object') {
      const errObj = anyErr.error;

      // Direct message fields from backend API schemas
      const backendMessage =
        errObj.message ||
        errObj.detail ||
        errObj.errorMessage ||
        errObj.error_description ||
        (typeof errObj.error === 'string' ? errObj.error : null) ||
        errObj.error?.message ||
        errObj.error?.detail ||
        errObj.title ||
        errObj.data?.message ||
        errObj.data?.detail ||
        errObj.data?.error;

      if (typeof backendMessage === 'string' && backendMessage.trim()) {
        return backendMessage.trim();
      }

      // Backend array of validation/business errors (e.g., Spring Boot, NestJS, Express, FastAPI)
      if (Array.isArray(errObj.errors) && errObj.errors.length > 0) {
        const first = errObj.errors[0];
        if (typeof first === 'string' && first.trim()) {
          return first.trim();
        }
        if (first && typeof first === 'object') {
          const nestedMsg = first.message || first.msg || first.detail || first.errorMessage;
          if (typeof nestedMsg === 'string' && nestedMsg.trim()) {
            return nestedMsg.trim();
          }
        }
      }

      if (Array.isArray(errObj.messages) && errObj.messages.length > 0) {
        const first = errObj.messages[0];
        if (typeof first === 'string' && first.trim()) {
          return first.trim();
        }
      }
    }
  }

  // ── PRIORITY 2: Direct error properties (custom errors or non-standard wrappers) ──
  const directMessage = anyErr.errorMessage || anyErr.detail;
  if (typeof directMessage === 'string' && directMessage.trim()) {
    return directMessage.trim();
  }

  if (typeof anyErr.message === 'string' && anyErr.message.trim()) {
    const msg = anyErr.message.trim();
    // Exclude generic Angular boilerplate: "Http failure response for http://...: 400 Bad Request"
    if (!msg.startsWith('Http failure response for')) {
      return msg;
    }
  }

  // ── PRIORITY 3: HTTP status-specific clear fallback messages ──
  if (typeof anyErr.status === 'number') {
    switch (anyErr.status) {
      case 0:
        return 'Unable to reach the server. Please check your internet connection or network status.';
      case 400:
        return fallbackMessage || 'Bad Request: The server could not understand the request.';
      case 401:
        return 'Session expired or unauthorized. Please sign in again.';
      case 403:
        return 'Access denied: You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found on the server.';
      case 408:
        return 'The request timed out. Please check your connection and try again.';
      case 409:
        return 'Conflict: The record has been modified or already exists.';
      case 422:
        return 'Validation failed: The provided data is invalid.';
      case 429:
        return 'Too many requests. Please slow down and try again.';
      case 500:
        return 'An internal server error occurred on the backend service. Please try again later.';
      case 502:
        return 'Bad Gateway: The server received an invalid response from upstream.';
      case 503:
        return 'Service Unavailable: The service is temporarily down for maintenance.';
      case 504:
        return 'Gateway Timeout: The server took too long to respond.';
    }
  }

  // ── PRIORITY 4: Contextual frontend fallback ──
  return fallbackMessage;
}
